import type { IMaterial } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';
import { getBasePartId, type Part } from './partUtils';

export interface MaterialGroup {
    material: IMaterial;
    count: number;
    materialNumbers: number[];
    signature: string;
}

export const createMaterialSignature = (material: IMaterial): string => {
    if (!material.children || material.children.length === 0) {
        return `empty-${material.width}x${material.height}`;
    }

    // Sort parts by position and size to create consistent signature
    const sortedParts = [...material.children].sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        if (a.x !== b.x) return a.x - b.x;
        if (a.width !== b.width) return a.width - b.width;
        return a.height - b.height;
    });

    // Create signature from sorted parts
    const partSignatures = sortedParts
        .map(
            (part) =>
                `${part.x || 0},${part.y || 0},${part.width}x${part.height}${part.rotated ? 'R' : ''
                }`
        )
        .join('|');

    return `${material.width}x${material.height}:${partSignatures}`;
};

export const groupMaterialsByPattern = (usedMaterials: IMaterial[]): MaterialGroup[] => {
    const groups = new Map<string, MaterialGroup>();

    usedMaterials.forEach((material, index) => {
        const signature = createMaterialSignature(material);

        if (groups.has(signature)) {
            const group = groups.get(signature)!;
            group.count++;
            group.materialNumbers.push(index + 1);
        } else {
            groups.set(signature, {
                material: material,
                count: 1,
                materialNumbers: [index + 1],
                signature: signature,
            });
        }
    });

    return Array.from(groups.values());
};

export const createMaterialCopy = (
    originalMaterial: IMaterial,
    materialIndex: number,
    remainingParts: IMaterial[],
    allParts: Part[]
): IMaterial => {
    const materialCopy: IMaterial = {
        ...originalMaterial,
        id: `material-${materialIndex}`,
        name: `Material ${materialIndex}`,
        children: [],
    };

    if (!originalMaterial.children) {
        return materialCopy;
    }

    let availableParts = [...remainingParts];

    originalMaterial.children.forEach((originalChild) => {
        const basePartId = getBasePartId(originalChild.id);
        const originalPart = allParts.find((p) => p.id === basePartId);

        if (originalPart) {
            const matchingPartIndex = availableParts.findIndex((part) => {
                const partBaseId = getBasePartId(part.id);
                return partBaseId === basePartId;
            });

            if (matchingPartIndex !== -1) {
                const matchingPart = availableParts[matchingPartIndex];

                const newChild: IMaterial = {
                    ...originalChild,
                    id: matchingPart.id,
                    width: originalChild.width,
                    height: originalChild.height,
                };

                materialCopy.children!.push(newChild);
                availableParts.splice(matchingPartIndex, 1);
            }
        }
    });

    return materialCopy;
};

export const getPatternPartsInfo = (pattern: IMaterial, allParts: Part[]) => {
    const partsInfo = new Map<
        string,
        { count: number; width: number; height: number }
    >();

    if (pattern.children) {
        pattern.children.forEach((child) => {
            const basePartId = getBasePartId(child.id);
            const originalPart = allParts.find((p) => p.id === basePartId);

            if (originalPart) {
                const key = `${originalPart.width}x${originalPart.height}`;

                if (!partsInfo.has(key)) {
                    partsInfo.set(key, {
                        count: 0,
                        width: originalPart.width,
                        height: originalPart.height,
                    });
                }

                partsInfo.get(key)!.count += 1;
            }
        });
    }

    return partsInfo;
};

export const calculateMaxRepeats = (
    remainingParts: IMaterial[],
    patternPartsInfo: Map<string, any>,
    allParts: Part[]
): number => {
    const remainingByType = new Map<string, number>();

    remainingParts.forEach((part) => {
        const basePartId = getBasePartId(part.id);
        const originalPart = allParts.find((p) => p.id === basePartId);

        if (originalPart) {
            const key = `${originalPart.width}x${originalPart.height}`;
            remainingByType.set(key, (remainingByType.get(key) || 0) + 1);
        }
    });

    let maxRepeats = Infinity;

    patternPartsInfo.forEach((patternInfo, partKey) => {
        const availableCount = remainingByType.get(partKey) || 0;
        const neededPerPattern = patternInfo.count;

        if (neededPerPattern > 0) {
            const possibleRepeats = Math.floor(availableCount / neededPerPattern);
            maxRepeats = Math.min(maxRepeats, possibleRepeats);
        }
    });

    return maxRepeats === Infinity ? 0 : Math.max(0, maxRepeats);
};